import React from 'react'
import { Dialog } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { CircularProgress } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { formatNormLabel } from '../../utils/normLabels'
import { DetailButton } from '../common/detail'
import '../common/detail/detail.css'

type ConfirmationModalProps = {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  selectedCategory: string | null
  loading?: boolean
  currency?: string
  frameworks?: string[]
}

const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
  selectedCategory,
  loading,
  currency,
  frameworks = [],
}) => {
  const { t } = useTranslation()
  const label = selectedCategory ? formatNormLabel(selectedCategory) : ''

  return (
    <Dialog
      open={isOpen}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      PaperProps={{ className: 'incyia-modal-paper' }}
    >
      <div className="incyia-modal-head">
        <div>
          <h3>{t('selectNormPage.confirmTitle')}</h3>
          <p>{t('selectNormPage.confirmMessage', { category: label })}</p>
        </div>
        <button
          type="button"
          className="incyia-modal-x"
          onClick={onClose}
          aria-label="Close"
        >
          <CloseIcon sx={{ fontSize: 18 }} />
        </button>
      </div>

      <div className="incyia-modal-body">
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 10,
            fontFamily: 'Lato, sans-serif',
          }}
        >
          <div>
            <div
              style={{
                fontSize: 11,
                fontWeight: 700,
                letterSpacing: '0.04em',
                textTransform: 'uppercase',
                color: '#8b99b4',
                marginBottom: 4,
              }}
            >
              {t('selectNormPage.normTitle', 'Base security norm')}
            </div>
            <div style={{ fontSize: 15, fontWeight: 700, color: '#00146d' }}>
              {label}
            </div>
          </div>
          {currency ? (
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: '#8b99b4',
                  marginBottom: 4,
                }}
              >
                {t('selectNormPage.currencyTitle', 'Currency')}
              </div>
              <div style={{ fontSize: 14.5, fontWeight: 600, color: '#0b1e2d' }}>
                {currency}
              </div>
            </div>
          ) : null}
          {frameworks.length > 0 ? (
            <div>
              <div
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  letterSpacing: '0.04em',
                  textTransform: 'uppercase',
                  color: '#8b99b4',
                  marginBottom: 6,
                }}
              >
                {t(
                  'selectNormPage.frameworksTitle',
                  'Industrial frameworks'
                )}
              </div>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {frameworks.map((f) => (
                  <span
                    key={f}
                    style={{
                      height: 24,
                      padding: '0 10px',
                      borderRadius: 999,
                      background: '#e1f4f0',
                      color: '#076b5c',
                      fontSize: 11.5,
                      fontWeight: 700,
                      display: 'inline-flex',
                      alignItems: 'center',
                    }}
                  >
                    {formatNormLabel(f)}
                  </span>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>

      <div className="incyia-modal-foot">
        <DetailButton variant="ghost" onClick={onClose}>
          {t('cancel', { defaultValue: 'Cancel' })}
        </DetailButton>
        <DetailButton onClick={onConfirm} disabled={loading}>
          {loading ? (
            <CircularProgress size={18} sx={{ color: '#fff' }} />
          ) : (
            t('confirm', { defaultValue: 'Confirm' })
          )}
        </DetailButton>
      </div>
    </Dialog>
  )
}

export default ConfirmationModal
